import React, { useEffect, useMemo } from 'react'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import { useModal } from '../../../../components/useModal'
import { useExportToXLS } from '../../../../lib/hooks/useExportToXLS'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { useInView } from 'react-intersection-observer'
import { motion } from 'framer-motion'
import LoadingComponent from '../../../../components/LoadingComponent'
import GhostButton from '../../../../components/Buttons/GhostButton'
import { omit, startCase } from 'lodash'
import CustomersUploadColumnsMapper from '../../../../components/CustomersUpload/ColumnsMapper'
import { customersMapperMongoColumns } from '../../../../lib/utils/customersMapperMongoColumns'
import { ReactComponent as LoadingIcon } from './../../../../images/icons/loading_icon.svg'
import { useGetThirdPartyVendorAccounts } from '../../../../api/dealership/dsp/third_party/useGetThirdPartyVendorAccounts'
import { SelectInput, Button } from '@demandlocal/components'
import { useVerifyFacebookLeadsAccount } from '../../../../api/dealership/dsp/third_party/useVerifyFacebookLeadsAccount'
import { useForceRefreshThirdPartyVendorAccounts } from '../../../../api/dealership/dsp/third_party/useForceRefreshThirdPartyVendorAccounts'
import toast from 'react-hot-toast'
import { queryClient } from '../../../../api/queryClient'

type Props = {
  databaseColumns?: Array<string>
  onSave: (values: FieldValues) => void
}

const FacebookLeadsFileMapper = (props: Props): JSX.Element => {
  const { databaseColumns, onSave } = props
  const { closeModal } = useModal()
  const { exportToCSV } = useExportToXLS()

  const { data: vendorAccounts } = useGetThirdPartyVendorAccounts(7)
  const {
    mutateAsync: forceRefreshThirdPartyAccount,
    isLoading: isRefetchingVendorAdAccounts,
  } = useForceRefreshThirdPartyVendorAccounts()
  const dealership = useDealership()

  const {
    mutate,
    data: { data } = { data: [] },
    isLoading,
    isSuccess,
  } = useVerifyFacebookLeadsAccount()

  const [ref, inView, entry] = useInView({
    threshold: 1,
    triggerOnce: true,
    fallbackInView: true,
  })

  const formContext = useForm({
    mode: 'onSubmit',
  })

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, errors, isSubmitSuccessful },
  } = formContext

  const vendor_account_id = watch('vendor_account_id')

  useEffect(() => {
    if (vendor_account_id) {
      mutate({
        params: {
          account_id: vendor_account_id?.value,
          dealership_id: dealership?.id,
        },
      })
    }
  }, [dealership?.id, mutate, vendor_account_id])

  const columns = useMemo(() => {
    const uniqueKeys = new Set()
    data.forEach((row: Record<string, unknown>) => {
      Object.keys(row).forEach((key) => uniqueKeys.add(key))
    })
    return Array.from(uniqueKeys).map((key) => {
      return {
        accessor: key,
        Header: key,
        columns: [{ accessor: key, Header: key }],
      }
    })
  }, [data])

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const rows = omit(values, 'spreadsheet_id')
    const rowsMapping = Object.entries(rows)
      .filter(([key, value]) => value)
      .map(([key, value]) => {
        return { [key]: value.value }
      })
    await onSave({
      rowsMapping,
      id: values.vendor_account_id?.value,
      value: values.vendor_account_id?.value,
      label: values?.vendor_account_id?.label,
    })
    closeModal()
  }

  return (
    <FormProvider {...formContext}>
      <form
        ref={ref}
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col pt-12 font-Spartan h-full'}>
        <div className={'flex flex-1 flex-col px-12 overflow-hidden'}>
          <div className={'font-bold text-[28px] text-primary-xiketic'}>
            Import Facebook Leads data
          </div>
          <div className={'pt-8 pb-8 border-b-2 flex flex-col gap-8'}>
            <div className={'w-1/2 flex items-center'}>
              <SelectInput
                name={'vendor_account_id'}
                label={'Account'}
                options={vendorAccounts}
              />
            </div>
            <div className={'font-medium flex flex-row mt-2 text-xs text-web-gray'}>
              Can't see your account?{' '}
              <span
                onClick={async (event) => {
                  event.preventDefault()
                  if (isRefetchingVendorAdAccounts) return
                  const toastId = toast.loading(
                    'Refreshing list... This may take a while.',
                  )
                  await forceRefreshThirdPartyAccount({ vendor_id: 7 }).catch((err) => {
                    toast.error('Something went wrong. Please try again later.', {
                      id: toastId,
                    })
                  })
                  await queryClient.invalidateQueries([
                    'third_party',
                    'vendor_accounts',
                    7,
                  ])
                  toast.success('List refreshed successfully!', { id: toastId })
                }}
                className={`${
                  isRefetchingVendorAdAccounts
                    ? 'text-gainsboro-gray cursor-not-allowed'
                    : 'text-primary-persian-blue cursor-pointer hover:underline'
                } ml-2`}>
                Refresh list
              </span>
            </div>
          </div>
          {!isSuccess && data?.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={'flex flex-1 items-center justify-center flex-col gap-4'}>
              {isLoading ? (
                <LoadingComponent title={'Loading data...'} />
              ) : (
                <div className={'flex flex-col items-center gap-4'}>
                  <LoadingIcon />
                  <span className={'font-bold text-xs text-blue-primary'}>
                    Your data will appear here
                  </span>
                </div>
              )}
            </motion.div>
          )}
          {isSuccess && data?.length > 0 && (
            <div className={'flex flex-1 flex-col pt-8 relative'}>
              <div className={'flex flex-row justify-between items-center pb-3'}>
                <div className={'flex flex-col'}>
                  <span className={'font-bold text-base'}>Match rows</span>
                  <span className={'mt-2 font-medium text-sm'}>
                    For a precise upload, please match the table headers below
                    {data?.length > 200 && (
                      <span className={'ml-2 font-normal text-xs'}>
                        (Showing first 200 rows)
                      </span>
                    )}
                  </span>
                </div>
                <div>
                  <GhostButton
                    transparent={true}
                    onClick={(e) => {
                      e.preventDefault()
                      exportToCSV(
                        [(databaseColumns ?? [])?.map((column) => startCase(column))],
                        'dl_saas_template.csv',
                        true,
                        'csv',
                      )
                    }}>
                    Download CSV Template
                  </GhostButton>
                </div>
              </div>
              {errors.submitError && (
                <div
                  className={'pb-3 font-medium text-sm text-secondary-bittersweet-red'}>
                  {/** @ts-ignore **/}
                  {errors.submitError.message}
                </div>
              )}
              <div
                className={
                  'flex flex-1 flex-col max-h-[calc(100vh_-_450px)] overflow-auto flex-1'
                }>
                <CustomersUploadColumnsMapper
                  data={data}
                  columns={columns}
                  rowsOptions={customersMapperMongoColumns}
                />
                {!inView && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className={'flex flex-1 items-center justify-center'}>
                    Loading...
                  </motion.div>
                )}
              </div>
            </div>
          )}
          {isSuccess && data?.length === 0 && (
            <div className={'flex flex-1 items-center justify-center flex-col gap-4'}>
              <span className={'font-bold text-xs text-blue-primary'}>No data found</span>
            </div>
          )}
        </div>
        <div className={'h-20 items-center flex justify-between border-t-2 py-4 mx-12'}>
          <Button
            variant={'secondary'}
            disabled={isSubmitting && isSubmitSuccessful}
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
          <Button
            disabled={(isSubmitting && isSubmitSuccessful) || data?.length === 0}
            type={'submit'}>
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default FacebookLeadsFileMapper
